import { useRef, useState } from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { CalendarDays, Car, Check, Clock, Home, ImagePlus, Upload, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

export const ServiceField = ({ b, cls }) => (
  <Select value={b.form.service} onValueChange={(v) => b.set("service", v)}>
    <SelectTrigger className={cls} data-error={!!b.errors.service} data-testid="booking-service-select" aria-label="Select your service">
      <SelectValue placeholder="Choose a service" />
    </SelectTrigger>
    <SelectContent className="lux-menu" data-lenis-prevent>
      {(b.options?.services || []).map((s, i) => (
        <SelectItem key={s.name} value={s.name} className="lux-item" data-testid={`booking-service-option-${i}`}>
          {s.name} · {s.hours} hr{s.hours > 1 ? "s" : ""}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const TypeButton = ({ b, value, cls, icon: Icon, title, sub, testId }) => {
  const active = b.form.appt_type === value;
  return (
    <motion.button type="button" className={`${cls} type-btn`} data-active={active} data-error={!!b.errors.appt_type}
      onClick={() => b.set("appt_type", value)} whileTap={{ scale: 0.97 }} aria-pressed={active} data-testid={testId}>
      <span className="type-inner"><Icon strokeWidth={1.5} /><span><b>{title}</b><small>{sub}</small></span></span>
      {active ? <span className="type-check"><Check strokeWidth={3} /></span> : null}
    </motion.button>
  );
};

export const DateField = ({ b, cls }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={cls} data-error={!!b.errors.date} data-empty={!b.form.date}
          data-testid="booking-date-button" aria-label="Pick a date">
          <span>{b.form.date ? format(b.form.date, "EEE, MMMM d, yyyy") : "Select a date"}</span>
          <CalendarDays className="field-icon" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="lux-pop w-auto p-0" align="start" data-lenis-prevent data-testid="booking-date-popover">
        <Calendar
          mode="single"
          selected={b.form.date}
          onSelect={(d) => { if (d) { b.set("date", d); setOpen(false); } }}
          disabled={(d) => !b.minDate || d < b.minDate || d > b.maxDate}
          fromDate={b.minDate || undefined}
          toDate={b.maxDate || undefined}
          defaultMonth={b.form.date || b.minDate || undefined}
          initialFocus
        />
        <p className="pop-note">No same-day bookings · up to 90 days ahead</p>
      </PopoverContent>
    </Popover>
  );
};

export const TimeField = ({ b, cls }) => {
  const open = b.slots.filter((s) => s.available);
  const placeholder = !b.form.date ? "Pick a date first" : b.slotsLoading ? "Loading times…"
    : open.length ? "Select a time" : "Fully booked — try another day";
  return (
    <Select value={b.form.time} onValueChange={(v) => b.set("time", v)} disabled={!b.form.date || !open.length}>
      <SelectTrigger className={cls} data-error={!!b.errors.time} data-testid="booking-time-select" aria-label="Select a time">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="lux-menu" data-lenis-prevent>
        {open.map((s) => (
          <SelectItem key={s.time} value={s.time} className="lux-item" data-testid={`booking-time-option-${s.time}`}>
            {s.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const PhotoField = ({ b, cls, showPrompt }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const has = b.photos.length > 0;
  const onDrop = (e) => { e.preventDefault(); setDrag(false); b.addPhotos(e.dataTransfer.files); };
  return (
    <div className={`${cls} drop`} data-has={has} data-drag={drag} data-error={!!b.errors.photos}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
      onClick={() => !has && input.current?.click()} role="button" tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && input.current?.click()} data-testid="booking-photo-dropzone">
      <input ref={input} type="file" hidden multiple accept="image/jpeg,image/png,image/webp"
        onChange={(e) => { b.addPhotos(e.target.files); e.target.value = ""; }} data-testid="booking-photo-input" />
      {has ? (
        <div className="thumbs">
          {b.photos.map((p, i) => (
            <motion.div key={p.url} className="thumb" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <img src={p.url} alt={`Hair photo ${i + 1}`} />
              <button type="button" onClick={(e) => { e.stopPropagation(); b.removePhoto(i); }}
                aria-label={`Remove photo ${i + 1}`} data-testid={`booking-photo-remove-${i}`}><X /></button>
            </motion.div>
          ))}
          {b.photos.length < 5 ? (
            <button type="button" className="thumb add" onClick={(e) => { e.stopPropagation(); input.current?.click(); }}
              data-testid="booking-photo-add-more"><ImagePlus /><span>Add</span></button>
          ) : null}
          <span className="thumb-count">{b.photos.length}/5 photos</span>
        </div>
      ) : showPrompt ? (
        <div className="drop-prompt">
          <Upload />
          <p><em>Upload Clear Photos</em> of Your Hair<br />(front, back, sides)</p>
          <small>JPG, PNG or WebP · Max 5 photos</small>
        </div>
      ) : null}
    </div>
  );
};

export const NotesField = ({ b, cls, lux }) => (
  <textarea className={`${cls} ov-textarea`} value={b.form.notes} data-error={!!b.errors.notes}
    onChange={(e) => b.set("notes", e.target.value)} maxLength={3000}
    placeholder={lux ? "Tell me which Lux Style look you want (required)…" : "Tell me anything I should know..."}
    data-testid="booking-notes-input" aria-label="Additional notes" />
);

export const InfoField = ({ label, cls, type = "text", value, onChange, placeholder, error, testId, autoComplete }) => (
  <label className="info-field">
    <span>{label}</span>
    <input className={cls} type={type} value={value} onChange={onChange} placeholder={placeholder}
      data-error={!!error} data-testid={testId} autoComplete={autoComplete} />
  </label>
);

export const LuxSelect = ({ b, cls }) => (
  <div className="info-field">
    <span>Choose your Lux Style *</span>
    <Select value={b.form.lux_style} onValueChange={(v) => b.set("lux_style", v)}>
      <SelectTrigger className={cls} data-error={!!b.errors.lux_style} data-testid="booking-lux-style-select">
        <SelectValue placeholder="Select your style" />
      </SelectTrigger>
      <SelectContent className="lux-menu" data-lenis-prevent>
        {(b.options?.lux_styles || []).map((s) => <SelectItem key={s} value={s} className="lux-item">{s}</SelectItem>)}
      </SelectContent>
    </Select>
    <p className="info-note">Retwist + Lux Style starts at $60 — detailed styles may have an additional fee, confirmed before booking.</p>
  </div>
);

export const AgreeBox = ({ b, cls }) => (
  <button type="button" role="checkbox" aria-checked={b.form.agree} className={`${cls} agree-box`}
    data-checked={b.form.agree} data-error={!!b.errors.agree} onClick={() => b.set("agree", !b.form.agree)}
    aria-label="I have read and agree to the House Rules" data-testid="booking-agree-checkbox">
    {b.form.agree ? <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}><Check strokeWidth={3.5} /></motion.span> : null}
  </button>
);

export const typeProps = {
  studio: { icon: Home, title: "In-Studio", sub: "(Washington, DC)", testId: "booking-type-studio" },
  mobile: { icon: Car, title: "Mobile", sub: "(Travel fees apply)", testId: "booking-type-mobile" },
};
export { Clock };
